import { useEffect, useRef } from "react";

type UseHidBarcodeScanOptions = {
  enabled: boolean;
  onScan: (code: string) => void;
};

export const useHidBarcodeScan = ({
  enabled,
  onScan,
}: UseHidBarcodeScanOptions) => {
  const bufferRef = useRef("");
  const lastKeyAtRef = useRef(0);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) {
      bufferRef.current = "";
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          target.isContentEditable
        ) {
          return;
        }
      }

      if (event.key === "Enter") {
        const code = bufferRef.current.trim();
        bufferRef.current = "";
        if (code) {
          event.preventDefault();
          onScanRef.current(code);
        }
        return;
      }

      if (
        event.key.length !== 1 ||
        event.ctrlKey ||
        event.altKey ||
        event.metaKey
      ) {
        return;
      }

      const now = Date.now();
      if (now - lastKeyAtRef.current > 750) {
        bufferRef.current = "";
      }
      lastKeyAtRef.current = now;
      bufferRef.current += event.key;
      event.preventDefault();
    };

    const onPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          target.isContentEditable
        ) {
          return;
        }
      }
      const text = event.clipboardData?.getData("text")?.replace(/\s/g, "");
      if (!text) {
        return;
      }
      event.preventDefault();
      bufferRef.current = "";
      onScanRef.current(text);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("paste", onPaste);
    };
  }, [enabled]);
};
